import { type Env } from "../http";
import { safeStringify } from "../json";
import {
	type AuthenticationResponse,
	Permission,
	isAuthorized,
	makeMessageAuthenticationRejected,
} from "../moment-graphql-api/authorization";
import { type Cell, type Page } from "../prosemirror-serialization";
import { type MutationDuplicatePageResponse, mutationDuplicatePage } from "./mutationDuplicatePage";
import { type MutationNewPageResponse, mutationNewPage } from "./mutationNewPage";
import {
	type MutationSaveCanvasMetadataResponse,
	mutationSaveCanvasMetadata,
} from "./mutationSaveCanvasMetadata";
import { type MutationSavePageResponse, mutationSavePage } from "./mutationSavePage";
import { type QueryCanvasMetadataResponse, queryCanvasMetadata } from "./queryCanvasMetadata";
import { queryPageById } from "./queryPageById";

export * from "./mutationNewPage";
export * from "./mutationSaveCanvasMetadata";
export * from "./queryCanvasMetadata";
export * from "./authorization";
export * from "./mutationDuplicatePage";

export interface AuthenticationApiClient {
	isReadAuthorized(opts: {
		authorizationHeader: string | null;
		canvasID: string;
	}): Promise<AuthenticationResponse>;

	isWriteAuthorized(opts: {
		authorizationHeader: string | null;
		canvasID: string;
	}): Promise<AuthenticationResponse>;
}

export class MomentAuthenticationApiClient implements AuthenticationApiClient {
	private readonly _fetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

	constructor(
		private readonly _env: Env,
		_fetch?: ((input: RequestInfo | URL, init?: RequestInit) => Promise<Response>) | undefined
	) {
		this._fetch = _fetch ?? fetch;
	}

	public async isReadAuthorized(opts: { authorizationHeader: string | null; canvasID: string }) {
		if (!opts.authorizationHeader) {
			return makeMessageAuthenticationRejected(opts.canvasID, "unknown_user");
		}
		try {
			return this.queryAuthorized(opts.authorizationHeader, opts.canvasID, Permission.READ);
		} catch (error) {
			return makeMessageAuthenticationRejected(opts.canvasID, "unknown_user");
		}
	}

	public async isWriteAuthorized(opts: { authorizationHeader: string | null; canvasID: string }) {
		if (!opts.authorizationHeader) {
			return makeMessageAuthenticationRejected(opts.canvasID, "unknown_user");
		}
		try {
			return this.queryAuthorized(opts.authorizationHeader, opts.canvasID, Permission.WRITE);
		} catch (error) {
			return makeMessageAuthenticationRejected(opts.canvasID, "unknown_user");
		}
	}

	private queryAuthorized(
		authorizationHeader: string,
		canvasID: string,
		permissionNeeded: Permission
	) {
		return isAuthorized({
			env: this._env,
			fetch: this._fetch,
			authorizationHeader,
			authorizationInput: {
				canvasID,
				permissionNeeded,
			},
		});
	}
}

/**
 * IMPORTANT: This client must be WHOLLY SEPARATE from the `MomentGraphQLPageApiClient`:
 *
 *   - The page client must never mutate the canvas metadata.
 *   - The metadata client must never mutate the page data.
 *   - If either of these things happen, the database will get out of sync with the collaboration
 *     servers, and we will have no way of reconciling them.
 */
export class MomentGraphQLMetadataApiClient {
	private readonly _fetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

	constructor(
		private readonly _env: Env,
		_fetch: ((input: RequestInfo | URL, init?: RequestInit) => Promise<Response>) | undefined,
		private _persistenceAuthorizationHeader: string | undefined
	) {
		this._fetch = _fetch ?? fetch;
	}

	get persistenceAuthorizationHeader(): string | undefined {
		return this._persistenceAuthorizationHeader;
	}

	set persistenceAuthorizationHeader(authorizationHeader: string) {
		this._persistenceAuthorizationHeader = authorizationHeader;
	}

	public async queryCanvasMetadata(opts: {
		canvasID: string;
		authorizationHeader: string;
	}): Promise<QueryCanvasMetadataResponse> {
		return queryCanvasMetadata({
			...opts,
			env: this._env,
			fetch: this._fetch,
			authorizationHeader: opts.authorizationHeader,
		});
	}

	public async mutationSaveCanvasMetadata(opts: {
		canvasID: string;
		title: string;
		owner: string;
		isTemplate: boolean;
		organizationID: string;
		archived: boolean;
		pages: { id: string; title: string; archived: boolean; parentPageID: string | null }[];
		version: number;
	}): Promise<MutationSaveCanvasMetadataResponse> {
		return mutationSaveCanvasMetadata({
			...opts,
			env: this._env,
			fetch: this._fetch,
			authorizationHeader: this._persistenceAuthorizationHeader,
		});
	}

	public async mutationNewPage(opts: {
		canvasID: string;
		title?: string;
	}): Promise<MutationNewPageResponse> {
		return mutationNewPage({
			...opts,
			env: this._env,
			fetch: this._fetch,
			authorizationHeader: this._persistenceAuthorizationHeader,
		});
	}

	public async mutationDuplicatePage(opts: {
		pageID: string;
		canvasID: string;
		title: string;
	}): Promise<MutationDuplicatePageResponse> {
		return mutationDuplicatePage({
			...opts,
			env: this._env,
			fetch: this._fetch,
			authorizationHeader: this._persistenceAuthorizationHeader,
		});
	}
}

/**
 * IMPORTANT: This client must be WHOLLY SEPARATE from the `MomentGraphQLMetadataApiClient`:
 *
 *   - The page client must never mutate the canvas metadata.
 *   - The metadata client must never mutate the page data.
 *   - If either of these things happen, the database will get out of sync with the collaboration
 *     servers, and we will have no way of reconciling them.
 */
export class MomentGraphQLPageApiClient {
	private readonly _fetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

	constructor(
		private readonly _env: Env,
		_fetch?: ((input: RequestInfo | URL, init?: RequestInit) => Promise<Response>) | undefined,
		private _persistenceAuthorizationHeader?: string | undefined
	) {
		this._fetch = _fetch ?? fetch;
	}

	get persistenceAuthorizationHeader(): string | undefined {
		return this._persistenceAuthorizationHeader;
	}

	set persistenceAuthorizationHeader(authorizationHeader: string) {
		this._persistenceAuthorizationHeader = authorizationHeader;
	}

	public async queryPageById(opts: {
		authorizationHeader: string;
		pageId: string;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		cellFn: (cell: any) => Cell;
	}): Promise<Page> {
		return queryPageById({
			...opts,
			env: this._env,
			fetch: this._fetch,
		});
	}

	public async mutationSavePage(opts: {
		pageID: string;
		canvasID: string;
		authorID: string;
		cells: Cell[];
		version: number;
	}): Promise<MutationSavePageResponse> {
		const serializedCells = opts.cells.map((cell) => {
			const nodeAttrs = cell.nodeAttrs;
			if (nodeAttrs) {
				return {
					type: cell.type,
					cellName: cell.cellName,
					code: cell.code,
					id: cell.id,
					// Note: the schema requires nodeAttrs to be a string type.
					// node attrs are passed down this function as a record for ergonomic intentions.
					nodeAttrs: safeStringify(nodeAttrs),
				};
			}
			return {
				type: cell.type,
				cellName: cell.cellName,
				code: cell.code,
				id: cell.id,
			};
		});
		return mutationSavePage({
			...opts,
			cells: serializedCells,
			env: this._env,
			fetch: this._fetch,
			authorizationHeader: this._persistenceAuthorizationHeader,
		});
	}
}
